<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { Search, ChatLineRound, VideoCamera, List, Check, Clock, Warning } from '@element-plus/icons-vue'
import { useChatStore } from '/src/stores/chat'

const chatStore = useChatStore()

const searchQuery = ref('')
const activeTaskTab = ref('owned')

const taskTabs = [
  { key: 'owned', label: '我执行的', icon: Check, color: '#3b82f6' },
  { key: 'tracked', label: '我跟踪的', icon: Clock, color: '#10b981' },
  { key: 'participated', label: '我参与的', icon: List, color: '#f59e0b' }
]

const currentTasks = computed(() => {
  const tasks = chatStore.myTasks[activeTaskTab.value] || []
  if (!searchQuery.value) return tasks
  const query = searchQuery.value.toLowerCase()
  return tasks.filter(t =>
    t.title?.toLowerCase().includes(query)
  )
})

const getPriorityColor = (priority) => {
  const colors = {
    P0: '#ef4444',
    P1: '#f59e0b',
    P2: '#3b82f6',
    P3: '#9ca3af'
  }
  return colors[priority] || '#9ca3af'
}

const getStatusIcon = (status) => {
  const icons = {
    completed: Check,
    in_progress: Clock,
    blocked: Warning
  }
  return icons[status] || Clock
}

const formatDeadline = (date) => {
  if (!date) return ''
  const now = new Date()
  const deadline = new Date(date)
  const diff = deadline - now
  const days = Math.ceil(diff / 86400000)

  if (days < 0) return '已超期'
  if (days === 0) return '今天'
  if (days === 1) return '明天'
  if (days <= 7) return `${days}天后`
  return deadline.toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' })
}

const isOverdue = (date) => {
  if (!date) return false
  return new Date(date) < new Date()
}

const handleTaskClick = (task) => {
  console.log('查看任务详情:', task)
}

const handleCreateSession = async (task) => {
  try {
    await chatStore.api?.createTaskSession(task.id)
    console.log('创建会话成功')
  } catch (err) {
    console.error('创建会话失败:', err)
  }
}

const handleCreateMeeting = (task) => {
  console.log('发起会议:', task)
}

onMounted(() => {
  chatStore.fetchMyTasks()
})

watch(() => chatStore.activeTab, (tab) => {
  if (tab === 'tasks') {
    chatStore.fetchMyTasks()
  }
})
</script>

<template>
  <div class="tasks-view">
    <div class="search-bar">
      <el-input
        v-model="searchQuery"
        placeholder="搜索任务..."
        :prefix-icon="Search"
        clearable
        size="small"
      />
    </div>

    <div class="task-tabs">
      <div
        v-for="tab in taskTabs"
        :key="tab.key"
        class="task-tab"
        :class="{ 'is-active': activeTaskTab === tab.key }"
        @click="activeTaskTab = tab.key"
      >
        <el-icon :style="{ color: tab.color }">
          <component :is="tab.icon" />
        </el-icon>
        <span>{{ tab.label }}</span>
        <span class="tab-count">{{ chatStore.myTasks[tab.key]?.length || 0 }}</span>
      </div>
    </div>

    <div class="tasks-list">
      <template v-if="currentTasks.length > 0">
        <div
          v-for="task in currentTasks"
          :key="task.id"
          class="task-item"
          @click="handleTaskClick(task)"
        >
          <div class="task-status">
            <el-icon :size="18" :class="task.status">
              <component :is="getStatusIcon(task.status)" />
            </el-icon>
          </div>
          <div class="task-content">
            <div class="task-title">{{ task.title }}</div>
            <div class="task-meta">
              <span
                class="priority-badge"
                :style="{ background: getPriorityColor(task.priority) }"
              >
                {{ task.priority }}
              </span>
              <span v-if="task.deadline" class="deadline" :class="{ overdue: isOverdue(task.deadline) }">
                <el-icon><Clock /></el-icon>
                {{ formatDeadline(task.deadline) }}
              </span>
            </div>
            <div class="task-stats" v-if="task.sessionCount || task.progress">
              <span v-if="task.sessionCount">
                <el-icon><ChatLineRound /></el-icon>
                {{ task.sessionCount }}个会话
              </span>
              <el-progress
                v-if="task.progress"
                :percentage="task.progress"
                :stroke-width="4"
                :show-text="false"
                style="width: 80px"
              />
            </div>
          </div>
          <div class="task-actions" @click.stop>
            <el-button size="small" text type="primary" @click="handleCreateSession(task)">
              <el-icon><ChatLineRound /></el-icon>
            </el-button>
            <el-button size="small" text @click="handleCreateMeeting(task)">
              <el-icon><VideoCamera /></el-icon>
            </el-button>
          </div>
        </div>
      </template>

      <div v-else class="empty-state">
        <el-icon :size="48" color="#d1d5db"><List /></el-icon>
        <p>暂无任务</p>
      </div>
    </div>

    <div class="bottom-action">
      <el-button type="primary" class="create-btn" @click="handleCreateSession({ id: null })">
        <el-icon><ChatLineRound /></el-icon>
        发起临时会话
      </el-button>
    </div>
  </div>
</template>

<style scoped>
.tasks-view {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.search-bar {
  padding: 12px;
  border-bottom: 1px solid #e5e7eb;
}

.task-tabs {
  display: flex;
  background: white;
  border-bottom: 1px solid #e5e7eb;
}

.task-tab {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 10px 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.task-tab:hover {
  background: #f9fafb;
}

.task-tab.is-active {
  background: #f0f9ff;
  border-bottom: 2px solid #0ea5e9;
}

.task-tab span {
  font-size: 12px;
  color: #6b7280;
}

.task-tab.is-active span {
  color: #0ea5e9;
}

.tab-count {
  font-weight: 600;
}

.tasks-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px 12px;
}

.task-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px;
  background: white;
  border-radius: 10px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.task-item:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.task-status {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: #f3f4f6;
  margin-top: 2px;
}

.task-status .completed {
  color: #10b981;
}

.task-status .in_progress {
  color: #3b82f6;
}

.task-status .blocked {
  color: #ef4444;
}

.task-content {
  flex: 1;
  min-width: 0;
}

.task-title {
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
  margin-bottom: 6px;
}

.task-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.priority-badge {
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  color: white;
}

.deadline {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #6b7280;
}

.deadline.overdue {
  color: #ef4444;
}

.task-stats {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 12px;
  color: #9ca3af;
}

.task-stats span {
  display: flex;
  align-items: center;
  gap: 4px;
}

.task-actions {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #9ca3af;
}

.empty-state p {
  margin: 12px 0;
}

.bottom-action {
  padding: 12px;
  border-top: 1px solid #e5e7eb;
}

.create-btn {
  width: 100%;
}
</style>
